import finnishMarketplaceConfigTranslations from '../translations/marketplaceConfig.fi.json';

const marketplaceConfigTranslations = {
  fi: finnishMarketplaceConfigTranslations,
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

const localizeExtendedDataFields = (fields = [], translations) => {
  if (!translations) {
    return fields;
  }

  return fields.map(field => {
    const translation = translations[field.key];
    if (!translation) {
      return field;
    }

    const localizedLabelConfig = config =>
      config && hasOwn(translation, 'label') ? { ...config, label: translation.label } : config;
    const enumOptions = field.enumOptions?.map(option => {
      const translatedLabel = translation.enumOptions?.[option.option];
      return translatedLabel ? { ...option, label: translatedLabel } : option;
    });

    return {
      ...field,
      ...(hasOwn(translation, 'label') ? { label: translation.label } : {}),
      ...(hasOwn(translation, 'helpText') ? { helpText: translation.helpText } : {}),
      ...(enumOptions ? { enumOptions } : {}),
      ...(field.filterConfig ? { filterConfig: localizedLabelConfig(field.filterConfig) } : {}),
      ...(field.showConfig ? { showConfig: localizedLabelConfig(field.showConfig) } : {}),
      ...(field.saveConfig ? { saveConfig: localizedLabelConfig(field.saveConfig) } : {}),
    };
  });
};

export const localizeListingFields = (fields = [], language) => {
  const translations = marketplaceConfigTranslations[language]?.listingFields;
  return localizeExtendedDataFields(fields, translations);
};

export const localizeListingTypes = (listingTypes = [], language) => {
  const translations = marketplaceConfigTranslations[language]?.listingTypes;
  if (!translations) {
    return listingTypes;
  }

  return listingTypes.map(listingType => {
    const label = translations[listingType.listingType];
    return label ? { ...listingType, label } : listingType;
  });
};

export const localizeCategories = (categories = [], language, parentPath = []) => {
  const translations = marketplaceConfigTranslations[language]?.categories;
  if (!translations) {
    return categories;
  }

  return categories.map(category => {
    const path = [...parentPath, category.id];
    const translation = translations[path.join('.')];
    const subcategories = localizeCategories(category.subcategories, language, path);
    return {
      ...category,
      ...(translation ? { name: translation } : {}),
      ...(category.subcategories ? { subcategories } : {}),
    };
  });
};

export const localizeUserTypes = (userTypes = [], language) => {
  const translations = marketplaceConfigTranslations[language]?.userTypes;
  if (!translations) {
    return userTypes;
  }

  return userTypes.map(userType => {
    const label = translations[userType.userType];
    return label ? { ...userType, label } : userType;
  });
};

export const localizeUserFields = (fields = [], language) => {
  const translations = marketplaceConfigTranslations[language]?.userFields;
  return localizeExtendedDataFields(fields, translations);
};

const localizeSearchConfig = (searchConfig, language) => {
  const translations = marketplaceConfigTranslations[language];
  if (!translations || !searchConfig?.defaultFilters) {
    return searchConfig;
  }

  const defaultFilters = searchConfig.defaultFilters.map(filter => {
    const fieldTranslation = translations.listingFields?.[filter.key];
    const optionTranslations =
      filter.schemaType === 'listingType'
        ? translations.listingTypes
        : fieldTranslation?.enumOptions;
    const label = fieldTranslation?.label;
    const options = filter.options?.map(option => {
      const translatedLabel = optionTranslations?.[option.option];
      return translatedLabel ? { ...option, label: translatedLabel } : option;
    });

    return {
      ...filter,
      ...(label ? { label } : {}),
      ...(options ? { options } : {}),
    };
  });

  return { ...searchConfig, defaultFilters };
};

export const localizeListingConfig = (listingConfig, language) => {
  const listingFields = listingConfig?.listingFields;
  const listingTypes = listingConfig?.listingTypes;
  if (!marketplaceConfigTranslations[language] || !listingConfig) {
    return listingConfig;
  }

  return {
    ...listingConfig,
    ...(Array.isArray(listingFields)
      ? { listingFields: localizeListingFields(listingFields, language) }
      : {}),
    ...(Array.isArray(listingTypes)
      ? { listingTypes: localizeListingTypes(listingTypes, language) }
      : {}),
  };
};

export const localizeMarketplaceConfig = (appConfig, language) => {
  if (!marketplaceConfigTranslations[language]) {
    return appConfig;
  }

  const categories = appConfig?.categoryConfiguration?.categories;
  const userTypes = appConfig?.user?.userTypes;
  const userFields = appConfig?.user?.userFields;

  return {
    ...appConfig,
    listing: localizeListingConfig(appConfig?.listing, language),
    categoryConfiguration: {
      ...appConfig?.categoryConfiguration,
      ...(Array.isArray(categories)
        ? { categories: localizeCategories(categories, language) }
        : {}),
    },
    user: {
      ...appConfig?.user,
      ...(Array.isArray(userTypes) ? { userTypes: localizeUserTypes(userTypes, language) } : {}),
      ...(Array.isArray(userFields) ? { userFields: localizeUserFields(userFields, language) } : {}),
    },
    search: localizeSearchConfig(appConfig?.search, language),
  };
};
